import React from 'react';

import {
  Box,
  Collapse,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';

import { TransitionGroup } from 'react-transition-group';
import DeleteIcon from '@mui/icons-material/Delete';

import { Link } from 'react-router-dom';

import _filter from 'lodash/filter';

import ConfirmationDialog from '../../components/ConfirmationDialog';
import ReportCategoryIcon from '../../components/ReportCategoryIcon';

import { EventFormStatus, IReportMetadata, ReportType } from '../../core/api/clients';
import useDeleteSafetyEventDraft from '../../core/api/hooks/safety-event/useDeleteSafetyEventDraft';
import useGetEventReportsByType from '../../core/api/hooks/event-report/useGetEventReportsByType';

function SafetyEventDrafts(): JSX.Element {
  const { data, refetch, isLoading } = useGetEventReportsByType({
    reportType: ReportType.SafetyEventReport,
    hours: 0,
    statuses: [EventFormStatus.Draft],
    isOwner: false,
  });
  const { mutate: deleteSafetyEventDraft } = useDeleteSafetyEventDraft();

  const [safetyEventDrafts, setSafetyEventDrafts] = React.useState<IReportMetadata[]>([]);
  const deletedDraftRef = React.useRef<number | null>(null);
  const [confDialogOpen, setConfDialogOpen] = React.useState<boolean>(false);
  const [deleteId, setDeleteId] = React.useState<number | undefined>(undefined);
  React.useEffect(() => {
    if (data !== undefined) {
      setSafetyEventDrafts(data);
    }
  }, [data]);

  React.useEffect(() => {
    if (deletedDraftRef.current) {
      deleteSafetyEventDraft(deletedDraftRef.current, {
        onSuccess: () => {
          refetch().then(() => {
            setDeleteId(undefined);
            deletedDraftRef.current = null;
          });
        },
      });
    }
  }, [safetyEventDrafts, deletedDraftRef]);

  const handleDelete = () => {
    if (deleteId !== undefined) {
      deletedDraftRef.current = deleteId;
      setSafetyEventDrafts((drafts) => _filter(drafts, (draft) => draft.id !== deleteId));
    }
  };

  const openDeleteConfirmationDialog = (id: number | undefined): void => {
    setConfDialogOpen(true);
    setDeleteId(id);
  };

  if (isLoading) {
    return (
      <LinearProgress />
    );
  }

  return (
    <List disablePadding>
      <ConfirmationDialog isOpen={confDialogOpen} setOpen={setConfDialogOpen} confirm={handleDelete} />
      <TransitionGroup>
        {safetyEventDrafts.map((draft) => (
          <Collapse key={draft.id}>
            <ListItem
              key={draft.id}
              secondaryAction={(
                <Box display="flex" sx={{ pr: 2 }}>
                  <IconButton onClick={() => openDeleteConfirmationDialog(draft.id)}>
                    <DeleteIcon />
                  </IconButton>
                </Box>
              )}
              disablePadding
              sx={{ py: 0 }}
            >
              <ListItemButton
                component={Link}
                to={`/form/Safety-event/${draft.id}/edit`}
                sx={{ padding: 2 }}
              >
                <ListItemIcon sx={{ ml: 2 }}>
                  <ReportCategoryIcon category={draft.category} />
                </ListItemIcon>
                <ListItemText primary={draft.title} secondary={draft.subtitle} />
              </ListItemButton>
            </ListItem>
          </Collapse>
        ))}
      </TransitionGroup>
    </List>
  );
}

export default SafetyEventDrafts;
